import { useEffect } from 'react';
import { api, ApiError } from '@/shared/api/client';
import { resetHomeEntrance } from '@/features/home/homeEntrance';
import { useAuthStore, AuthUser, CompanyInfo, CompanyMembership } from '@/shared/store/authStore';
import { syncAppearanceFromServer } from '@/features/settings/AppearancePage';
import { claimErpSession, endErpSession, sessionOwner, startErpSession } from '@/shared/erpSession';

type MeResponse = { user: AuthUser; company: CompanyInfo; companies: CompanyMembership[] };

export function useAuth() {
  const { user, company, companies, ready, setSession, setCompanies, setReady, clear } = useAuthStore();

  useEffect(() => {
    if (ready) return;
    let mounted = true;
    (async () => {
      try {
        const data = await api.get<MeResponse>('/auth/me');
        if (!mounted) return;
        // history-1: a session this tab started for another user or company
        // (signed in or switched in another tab) is dropped, with its lists
        // and its restored page, before anything renders.
        claimErpSession(sessionOwner(data.user.id, data.company.id));
        setSession(data.user, data.company);
        setCompanies(data.companies || []);
      } catch (err) {
        if (!(err instanceof ApiError) || err.status !== 401) {
          console.error('useAuth: /auth/me failed', err);
        }
        if (mounted) clear();
      } finally {
        if (mounted) setReady(true);
      }
    })();
    return () => { mounted = false; };
  }, [ready, setSession, setCompanies, setReady, clear]);

  // Once we have a logged-in user, pull the latest appearance prefs
  // from the server and apply them. initAppearance() in main.tsx
  // already applied the localStorage cache so the boot was instant;
  // this override only matters on a different browser/device, where
  // it swaps in the user's real choice within one round-trip.
  useEffect(() => {
    if (user?.id) void syncAppearanceFromServer();
  }, [user?.id]);

  async function logout() {
    // D1: before anything can navigate away (the location.href below).
    endErpSession();
    try { await api.post('/auth/logout'); } catch { /* ignore */ }
    clear();
    // Re-arm the Home entrance so the next login plays it again.
    resetHomeEntrance();
    window.location.href = '/login';
  }

  async function switchCompany(companyId: string) {
    const data = await api.post<MeResponse>('/auth/switch-company', { company_id: companyId });
    setSession(data.user, data.company);
    // D1: the old company's history entries are never replayed after the reload.
    startErpSession(sessionOwner(data.user.id, data.company.id));
    // Full reload to flush all cached queries scoped to the old tenant.
    window.location.href = '/';
  }

  return { user, company, companies, ready, isAuthenticated: !!user, logout, switchCompany };
}
